import { useCallback, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { TokenContext } from '@/Contexts/TokenContext';
import { UsersContext } from '@/Contexts/UsersContext';
import { ImpersonationResult } from '@/models/ImpersonationResult';
import { StartUserImpersonationProps } from '@/models/StartUserImpersonationProps';
import { StopUserImpersonationProps } from '@/models/StopUserImpersonationProps';
import { Locales, UserProps, UserType } from '@/models/User';
import { baseUrl } from '@/utils/constants';

import { useCustomFetch } from './useCustomFetch';
import { logError } from '@/utils/errorHandlerUtil';

export default function useUser() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { customFetch } = useCustomFetch();

  const { b2cId, authResult } = useContext(TokenContext);
  const { userCurrent, updateUserCurrent, updateUsersAll } = useContext(UsersContext);

  const getAllUsers = useCallback(async (): Promise<UserProps[]> => {
    const fetchUrl = `${baseUrl}users`;

    try {
      const response = await customFetch<UserProps[]>(fetchUrl, {
        method: 'GET',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    } catch (error) {
      logError('Error fetching all users.', error);
      return [];
    }
  }, [authResult, customFetch]);

  const updateUsers = useCallback(
    (usersToUpdate: UserProps | UserProps[]) => {
      const patchUsers = (users: UserProps[], patches: UserProps[]): UserProps[] => {
        const map = new Map(users.map((u) => [u.b2CId, u]));

        for (const patch of patches) {
          const existing = map.get(patch.b2CId);
          if (existing != null) {
            map.set(patch.b2CId, { ...existing, ...patch });
          }
        }

        return Array.from(map.values());
      };

      updateUsersAll((prev: UserProps[]) =>
        prev != null
          ? patchUsers(prev, Array.isArray(usersToUpdate) ? usersToUpdate : [usersToUpdate])
          : prev
      );
    },
    [updateUsersAll]
  );

  const updateCurrentPartnerId = useCallback(
    async (partnerId: number) => {
      if (b2cId == null) return null;

      const fetchUrl = `${baseUrl}users/${b2cId}/currentPartner`;
      try {
        const response = await customFetch<UserProps>(fetchUrl, {
          method: 'PATCH',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
          body: JSON.stringify(partnerId),
        });

        updateUserCurrent((prevValue) =>
          prevValue != null ? { ...prevValue, currentPartnerId: partnerId } : prevValue
        );

        return response;
      } catch (error) {
        logError(
          `Error updating currentPartnerId ${partnerId} for user with b2CId: ${b2cId}.`,
          error
        );
        return null;
      }
    },
    [authResult, b2cId, customFetch, updateUserCurrent]
  );

  const updatePreferredLanguage = useCallback(
    async (language: Locales): Promise<void> => {
      if (b2cId == null) return;

      const fetchUrl = `${baseUrl}users/${b2cId}/preferredLanguage`;
      try {
        await customFetch(fetchUrl, {
          method: 'PATCH',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
          body: JSON.stringify(language),
        });

        updateUserCurrent((prevValue) =>
          prevValue != null ? { ...prevValue, preferredLanguage: language } : prevValue
        );
      } catch (error) {
        logError(
          `Error updating preferredLanguage ${language} for user with b2CId: ${b2cId}.`,
          error
        );
      }
    },
    [authResult, b2cId, customFetch, updateUserCurrent]
  );

  const updateUserContactId = useCallback(
    async (contactId: number) => {
      if (b2cId == null) return null;

      const fetchUrl = `${baseUrl}users/${b2cId}/contact`;
      try {
        const response = await customFetch<UserProps>(fetchUrl, {
          method: 'PATCH',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
          body: JSON.stringify(contactId),
        });

        updateUserCurrent((prevValue) =>
          prevValue != null ? { ...prevValue, contactId: contactId } : prevValue
        );

        return response;
      } catch (error) {
        logError(`Error updating contactId ${contactId} for user with b2CId: ${b2cId}.`, error);
        return null;
      }
    },
    [authResult, b2cId, customFetch, updateUserCurrent]
  );

  const getUser = useCallback(
    async (b2CId: string): Promise<UserProps | null> => {
      if (b2CId == null) return null;

      const fetchUrl = `${baseUrl}users/${b2CId}`;
      try {
        const response = await customFetch<UserProps>(fetchUrl, {
          method: 'GET',
          headers: {
            accept: 'application/json',
            'content-type': 'application/json',
            Authorization: `Bearer ${authResult.accessToken}`,
          },
        });

        return response;
      } catch (error) {
        logError(`Error fetching user with b2CId: ${b2CId}.`, error);
        return null;
      }
    },
    [authResult, customFetch]
  );

  const postUser = useCallback(
    async (userInfo: UserProps): Promise<UserProps> => {
      const url = `${baseUrl}users`;

      const response = await customFetch<UserProps>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(userInfo),
      });

      return response;
    },
    [authResult, customFetch]
  );

  const resetUserToIntake = useCallback(
    async (b2CId: string): Promise<UserProps> => {
      const url = `${baseUrl}users/${b2CId}/reset`;

      const response = await customFetch<UserProps>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
      });

      return response;
    },
    [authResult, customFetch]
  );

  const startUserImpersonation = useCallback(
    async (userInfo: StartUserImpersonationProps): Promise<ImpersonationResult> => {
      const url = `${baseUrl}users/impersonation/start`;

      const impersonatee = await customFetch<UserProps>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(userInfo),
      });

      const updatedImpersonator = await getUser(
        userInfo?.impersonatorB2CId != null ? userInfo.impersonatorB2CId : ''
      );

      if (updatedImpersonator == null) {
        throw new Error('Impersonator user not found after starting impersonation.');
      }

      return { impersonatee, impersonator: updatedImpersonator };
    },
    [authResult, customFetch, getUser]
  );

  const stopUserImpersonation = useCallback(
    async (stopImpersonateRequest: StopUserImpersonationProps): Promise<ImpersonationResult> => {
      const url = `${baseUrl}users/impersonation/stop`;

      const updatedImpersonatee = await customFetch<UserProps>(url, {
        method: 'POST',
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
          Authorization: `Bearer ${authResult.accessToken}`,
        },
        body: JSON.stringify(stopImpersonateRequest),
      });

      const updatedImpersonator = await getUser(
        stopImpersonateRequest?.impersonatorB2CId != null
          ? stopImpersonateRequest.impersonatorB2CId
          : ''
      );

      if (updatedImpersonator == null) {
        throw new Error('Impersonator user not found after stopping impersonation.');
      }

      updateUsers([updatedImpersonatee, updatedImpersonator]);

      return {
        impersonatee: updatedImpersonatee,
        impersonator: updatedImpersonator,
      };
    },
    [authResult, customFetch, getUser, updateUsers]
  );

  useEffect(() => {
    if (userCurrent != null && userCurrent.partnerGroup?.id == null) {
      if (userCurrent.contactId != null && pathname.includes('sign')) {
        navigate('/dashboard');
      }

      if (userCurrent.userType == UserType.Applicant && userCurrent.partnerGroupId == null) {
        navigate('/signup');
      }
    }
  }, [userCurrent, pathname, navigate]);

  return {
    updateUsers,
    getUser,
    postUser,
    getAllUsers,
    updatePreferredLanguage,
    startUserImpersonation,
    stopUserImpersonation,
    updateCurrentPartnerId,
    updateUserContactId,
    resetUserToIntake,
  };
}
